import { useLocalization } from '@components/Localization'
import { useNotifications } from '@components/NotificationsProvider'
import { Box, theme } from '@gassan-ui'
import {
  CartItem,
  useAddArticleToCartMutation,
  useRemoveItemFromCartMutation,
  useUpdateCartItemSizeMutation
} from '@generated'
import { imgix } from '@lib/imigx'
import { AnimatePresence } from 'framer-motion'
import React, { FC } from 'react'
import * as ReactGA from 'react-ga'
import CartRow from '../CartRow'
import LoadingRow from '../LoadingRow'
import { useCart } from '@lib/hooks/useCart'

type Props = {
  items: CartItem[]
  loadingRow: string | null
  handleChangeQuantity: (cartItemId: string, quantity: number) => void
}

const CartList: FC<Props> = ({ handleChangeQuantity, loadingRow, items }) => {
  const { lang, messages } = useLocalization()
  const notifications = useNotifications()
  const cart = useCart()
  const reference = cart?.id

  const [, updateCartItemSize] = useUpdateCartItemSizeMutation()

  const [, removeItemFromCart] = useRemoveItemFromCartMutation()

  const [, addArticleToCart] = useAddArticleToCartMutation()

  const handleUpdateSize = async (cartItemId: string, size: string) => {
    try {
      await updateCartItemSize({ reference, cartItemId, size })
    } catch (error) {
      console.error(error)
      // show error notification
    }
  }

  const handleRemoveItemFromCart = async cartItemId => {
    if (!cart) {
      return
    }
    const item = cart.items.find(item => item.id === cartItemId)
    try {
      notifications.addNotification({
        variant: 'image-action-undo',
        label: item.article.brand,
        sublabel: item.article.description,
        image: imgix(item.article.images[0], { width: 180 }),
        description: lang === 'nl' ? 'Verwijderd uit winkelmand' : 'Removed from shopping cart',
        actionLabel: lang === 'nl' ? 'Herstel' : 'Undo',
        onClickAction: () => handleAddArticleToCart(item.article.id, item.quantity, item.size)
      })
      await removeItemFromCart({ reference, cartItemId })
      ReactGA.event({
        category: 'Ecommerce',
        action: 'removeFromCart'
      })
    } catch (error) {
      notifications.addNotification({
        variant: 'error',
        label: lang === 'nl' ? 'Artikel kon niet worden verwijderd' : 'Article could not be removed'
      })
    }
  }

  const handleAddArticleToCart = async (articleId: string, quantity: number, size?: string) => {
    try {
      await addArticleToCart({
        reference,
        articleId,
        quantity,
        size
      })
      ReactGA.event({
        category: 'Ecommerce',
        action: 'addToCart'
      })
    } catch (error) {
      notifications.addNotification({
        variant: 'error',
        label: messages.notifications.refreshAndTryAgain
      })
    }
  }

  return (
    <Box
      pt={{ _: '1rem', small: '1.75rem', xlarge: '2rem' }}
      borderTop={`1px solid ${theme.colors.shades[200]}`}
    >
      {items ? (
        <AnimatePresence>
          {items.map(item => {
            return (
              <CartRow
                key={item.id}
                item={item}
                variant={!cart ? 'loading' : loadingRow === item.id ? 'updating' : 'idle'}
                onChangeQuantity={quantity => handleChangeQuantity(item.id, quantity)}
                onChangeSize={size => handleUpdateSize(item.id, size)}
                onRemove={() => handleRemoveItemFromCart(item.id)}
              />
            )
          })}
        </AnimatePresence>
      ) : (
        [...Array(3)].map((_, key) => <LoadingRow key={key} />)
      )}
    </Box>
  )
}

export default CartList
